import { create } from "zustand";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchTeams, getActionLog } from "@/api/teamApi.ts";
import { useAuthStore } from "@/store/authStore.ts";
import { useMemo } from "react";
import { queryClient } from "@/store/queryConfig.ts";

export type LogEntryType =
    | "SERVER_CREATION"
    | "SERVER_DELETION"
    | "SERVER_START"
    | "SERVER_STOP"
    | "SERVER_RESTART"
    | "SERVER_RCON_COMMAND"
    | "SERVER_BACKUP"
    | "SERVER_BACKUP_DELETION"
    | "SERVER_ROLLBACK"
    | "SERVER_UPDATE"
    | "SERVER_SETTINGS_CHANGE"
    | "NODE_CREATION"
    | "NODE_REGISTRATION"
    | "NODE_DELETION"
    | "NODE_SETTINGS_CHANGE"
    | "NODE_UPDATE"
    | "TEAM_CREATION"
    | "TEAM_INVITATION"
    | "TEAM_JOIN"
    | "TEAM_LEAVE"
    | "TEAM_KICK"
    | "TEAM_PERMISSION_CHANGE"
    | "TEAM_NAME_CHANGE"
    | "TEAM_ICON_CHANGE"
    | "TEAM_APPLICATION_CREATION"
    | "TEAM_APPLICATION_DELETION"
    | "SETTING_PROFILE_UPDATE";

export type LogEntry = {
    id: string;
    type: LogEntryType;
    timestamp: string;
    teamId?: string;
    issuerId: string;
    affectedNodeId?: string;
    affectedServerId?: string;
    affectedUserId?: string;
    actionInformation?: string;
};

export type TeamActionLogPage = {
    content: LogEntry[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    numberOfElements: number;
    empty: boolean;
};

export type TeamMember = {
    id: string;
    name: string;
    email: string;
    iconBase64: string;
    permissions: Permission[];
    twoFAEnabled: boolean;
};

export type MemberInvite = {
    id: string;
    name: string;
    email: string;
    iconBase64: string;
    invitedAt: string;
    expired: boolean;
};

export type Permission =
    | "MANAGE_TEAM"
    | "ACCESS_NODES"
    | "MANAGE_NODES"
    | "MANAGE_SERVERS"
    | "ACCESS_SERVERS"
    | "MANAGE_PROFILES"
    | "ACCESS_PROFILES"
    | "ACCESS_TEAM";

export type Team = {
    id: string;
    name: string;
    iconBase64: string;
    owner: TeamMember;
    members: TeamMember[];
    applications: Record<string, Permission[]>;
    invitedMembers: MemberInvite[];
    inviteCode: string;
};

type TeamState = {
    activeTeamId: string | null;
    selectActiveTeam: (teamId: string | null) => void;
    ensureActiveTeam: (teams: Team[]) => void;
};

export const useTeamStore = create<TeamState>((set, get) => ({
    activeTeamId: null,
    selectActiveTeam: (teamId: string | null) => {
        const previousTeamId = get().activeTeamId;
        if (teamId !== previousTeamId) {
            if (teamId) {
                queryClient.invalidateQueries({ queryKey: ["servers"] }).then();
                queryClient.invalidateQueries({ queryKey: ["nodes"] }).then();
            }
            localStorage.setItem("lastActiveTeamId", teamId ?? "");
        }
        set({ activeTeamId: teamId });
    },
    ensureActiveTeam: (teams) => {
        if (!get().activeTeamId && teams.length > 0) {
            const lastActiveTeamId = localStorage.getItem("lastActiveTeamId");
            if (
                lastActiveTeamId &&
                teams.find((team) => team.id === lastActiveTeamId)
            ) {
                set({ activeTeamId: lastActiveTeamId });
            } else {
                const teamId = teams[0].id;
                localStorage.setItem("lastActiveTeamId", teamId ?? "");
                set({ activeTeamId: teamId });
            }
        } else if (teams.length > 0) {
            if (!teams.find((team) => team.id === get().activeTeamId)) {
                const teamId = teams[0].id;
                localStorage.setItem("lastActiveTeamId", teamId ?? "");
                set({ activeTeamId: teamId });
            }
        }
    },
}));

export const useTeamQuery = () => {
    // Only fetch teams when authenticated to avoid caching an empty list
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    return useQuery<Team[]>({
        queryKey: ["teams"],
        enabled: isAuthenticated,
        queryFn: async () => {
            const teams = await fetchTeams();
            useTeamStore.getState().ensureActiveTeam(teams);
            return teams;
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useActiveTeam = () => {
    const { data: teams } = useTeamQuery();
    const { activeTeamId, ensureActiveTeam } = useTeamStore();

    return useMemo(() => {
        ensureActiveTeam(teams ?? []);
        return teams?.find((team: Team) => team.id === activeTeamId) || null;
    }, [ensureActiveTeam, teams, activeTeamId]);
};

const buildEmptyActionLogPage = (
    page: number,
    size: number,
): TeamActionLogPage => ({
    content: [],
    page,
    size,
    totalElements: 0,
    totalPages: 0,
    first: page === 0,
    last: true,
    numberOfElements: 0,
    empty: true,
});

export const DEFAULT_ACTION_LOG_PAGE_SIZE = 20;

export const useTeamActionLogInfiniteQuery = (
    teamId: string | null | undefined,
    pageSize = DEFAULT_ACTION_LOG_PAGE_SIZE,
) => {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

    return useInfiniteQuery<TeamActionLogPage, Error>({
        queryKey: ["teamActionLog", { teamId: teamId ?? null, pageSize }],
        enabled: isAuthenticated && !!teamId,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            if (!teamId) {
                return buildEmptyActionLogPage(0, pageSize);
            }
            const currentPage =
                typeof pageParam === "number" ? pageParam : Number(pageParam);
            return (await getActionLog(
                teamId,
                currentPage,
                Math.max(pageSize, 1),
            )) as TeamActionLogPage;
        },
        getNextPageParam: (lastPage) => {
            if (!lastPage) return undefined;
            if (lastPage.last) return undefined;
            const nextPage = lastPage.page + 1;
            if (lastPage.totalPages && nextPage >= lastPage.totalPages) {
                return undefined;
            }
            return nextPage;
        },
        placeholderData: (previousData) => previousData,
    });
};

export const getTeamMemberPermissions = (
    team: Team | null,
    userId?: string,
): Permission[] => {
    if (!team) return [];
    if (team.owner.id === userId) {
        return team.owner.permissions ?? [];
    }
    return (
        team.members
            .filter((member) => member.id === userId)
            .map((member) => member.permissions)
            .flat() ?? []
    );
};

export const hasPermission = (
    team: Team | null,
    userId: string | undefined,
    permission: Permission,
) => {
    return getTeamMemberPermissions(team, userId).includes(permission);
};
